import React, { useState } from 'react';
import { useQuery } from 'hooks/apollo';
import { gql } from '@apollo/client';

import { Button, Input } from 'antd';
import Page from 'components/Page';
import Table from 'components/Table';

import globalMessages from 'translations/messages';
import useFormatMessage from 'hooks/useFormatMessage';
import Drawer from 'components/Drawer';
import Order from 'components/Order';
import StatusTag from 'components/StatusTag';
import CurrencyFormat from 'components/CurrencyFormat';
import { fullOrderInfo } from 'shared/utils/order';
import { PaymentStatus } from 'shared/prisma/types';
import { dayjs } from 'shared/utils/date';

const QUERY = gql`
  query {
    orders {
      ${fullOrderInfo}
      customer {
        id
        name
      }
    }
  }
`;

const columns = [
  {
    dataIndex: 'createdAt',
    title: 'Created',
    type: 'datetime',
    defaultSortOrder: 'descend',
  },
  {
    dataIndex: 'orderNumber',
    title: 'Order',
    type: 'number',
    render: (orderNumber) => `#${orderNumber}`,
  },
  {
    dataIndex: ['customer', 'name'],
    title: 'Account',
  },
  {
    dataIndex: 'dataSource',
    title: 'Data source',
  },
  {
    dataIndex: 'amount',
    title: 'Amount',
    type: 'number',
    render: (amount, record) => amount > 0 && <CurrencyFormat currency={record.currency} value={amount} />,
  },
  {
    dataIndex: 'paymentStatus',
    title: 'Payment status',
    align: 'center',
    render: (status, record) => record.amount > 0 && <StatusTag status={status} />,
  },
  {
    dataIndex: 'paymentDate',
    title: 'Payment date',
    type: 'datetime',
    render: (paymentDate, record) => {
      if (paymentDate) {
        return dayjs(paymentDate).format('YYYY-MM-DD');
      }
      if (record.amount > 0 && record.paymentStatus === PaymentStatus.Pending && record.startDate && dayjs(record.startDate).isAfter(dayjs(), 'day')) {
        return `Scheduled: ${dayjs(record.startDate).format('YYYY-MM-DD')}`;
      }
      return null;
    },
  },
];

const Orders = () => {
  const $t = useFormatMessage();
  const { data, loading } = useQuery(QUERY);
  const [search, setSearch] = useState();
  const [showOrderDetails, setShowOrderDetails] = useState(false);

  const actions = (record) => (
    <Button onClick={() => setShowOrderDetails(record)}>
      Details
    </Button>
  );

  return (
    <Page>
      <Page.Header
        title="Orders"
      />
      <Page.Content>
        <Input.Search
          onSearch={setSearch}
          allowClear
          placeholder={$t(globalMessages.search)}
          style={{ marginBottom: '1em' }}
        />
        <Table
          loading={loading}
          columns={columns}
          dataSource={data?.orders}
          actions={actions}
          search={search}
        />
        <Drawer
          title="Order details"
          open={!!showOrderDetails}
          onClose={() => setShowOrderDetails(false)}
          footer={false}
        >
          {showOrderDetails && <Order order={showOrderDetails} />}
        </Drawer>
      </Page.Content>
    </Page>
  );
};

export default Orders;
